import {
    Close as CloseIcon,
    Psychology as PsychologyIcon,
} from '@mui/icons-material';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { capitalizeName } from '../../../shared/utils';
import { highlightText } from './ReviewCard';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface KeywordInsight {
    keyword: string;
    positiveCount: number;
    negativeCount: number;
    neutralCount: number;
    positiveIds: string[];
    negativeIds: string[];
}

interface AiReviewData {
    overallSentiment: 'positive' | 'negative' | 'neutral';
    overallReview: string;
    keywordInsights: KeywordInsight[];
}

interface AiReviewSummaryProps {
    aiReview: AiReviewData;
    onKeywordClick: (
        keyword: string,
        reviewIds: string[],
        sentiment: 'positive' | 'negative',
        positiveCount?: number,
        negativeCount?: number,
    ) => void;
    onShowMore?: (reviewId: number) => void;
    reviews: Array<{
        id: number;
        reviewText: string;
        createdBy: {
            firstName: string;
            lastName: string;
        };
    }>;
}

const AiReviewSummary: React.FC<AiReviewSummaryProps> = ({
    aiReview,
    onKeywordClick,
    onShowMore,
    reviews,
}) => {
    const [selectedKeyword, setSelectedKeyword] =
        useState<KeywordInsight | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width: 770px)');

    const getUnifiedKeywordStyle = (insight: KeywordInsight) => {
        const total =
            insight.positiveCount +
            insight.negativeCount +
            insight.neutralCount;
        const positiveRatio = insight.positiveCount / total;
        const negativeRatio = insight.negativeCount / total;

        // Strongly positive (>70% positive)
        if (positiveRatio > 0.7) {
            return {
                backgroundColor: theme.palette.success.main + '20', // 20% opacity
                borderColor: theme.palette.success.main,
                textColor:
                    theme.palette.success.dark || theme.palette.success.main,
            };
        }
        // Strongly negative (>70% negative)
        if (negativeRatio > 0.7) {
            return {
                backgroundColor: theme.palette.error.main + '20', // 20% opacity
                borderColor: theme.palette.error.main,
                textColor: theme.palette.error.dark,
            };
        }
        // Moderately positive (50-70% positive)
        if (positiveRatio > 0.5) {
            return {
                backgroundColor: theme.palette.success.main + '15', // 15% opacity
                borderColor: theme.palette.success.main + '80', // 80% opacity
                textColor: theme.palette.success.main,
            };
        }
        // Moderately negative (50-70% negative)
        if (negativeRatio > 0.5) {
            return {
                backgroundColor: theme.palette.error.main + '15', // 15% opacity
                borderColor: theme.palette.error.main + '80', // 80% opacity
                textColor: theme.palette.error.main,
            };
        }
        // Neutral/mixed
        return {
            backgroundColor: theme.palette.custom.cardActionBackground,
            borderColor: theme.palette.custom.columnBorder,
            textColor: theme.palette.text.primary,
        };
    };

    const handleKeywordClick = (insight: KeywordInsight) => {
        setSelectedKeyword(insight);
    };

    const getSampleReviews = (insight: KeywordInsight) => {
        const allReviewIds = [...insight.positiveIds, ...insight.negativeIds];

        return reviews
            .filter((review) => allReviewIds.includes(review.id.toString()))
            .slice(0, 3);
    };

    const sortedKeywords = [...aiReview.keywordInsights].sort(
        (a, b) =>
            b.positiveCount +
            b.negativeCount +
            b.neutralCount -
            (a.positiveCount + a.negativeCount + a.neutralCount),
    );

    return (
        <Box sx={{ mb: 4, mt: 2 }}>
            <Box display='flex' alignItems='center' gap={1} mb={1}>
                <PsychologyIcon
                    sx={{
                        color: 'primary.main',
                        fontSize: isMobile ? '20px' : '24px',
                    }}
                />
                <Typography
                    variant='h6'
                    sx={{
                        fontWeight: 'bold',
                        color: 'text.primary',
                        fontSize: isMobile ? '16px' : '18px',
                        flex: 1,
                    }}
                >
                    Guest Feedback Overview
                </Typography>
            </Box>

            <Typography
                variant='caption'
                sx={{
                    color: 'text.primary',
                    fontSize: '11px',
                    display: 'block',
                    opacity: 0.8,

                    mb: 2,
                }}
            >
                AI-generated summary based on guest reviews
            </Typography>

            <Typography
                variant='body3'
                sx={{
                    color: 'text.primary',
                    lineHeight: 1.6,
                    mb: 3,
                }}
            >
                {aiReview.overallReview}
            </Typography>

            <Typography
                variant='caption'
                sx={{
                    color: 'text.primary',
                    fontSize: '12px',
                    mb: 2,
                    display: 'block',
                    opacity: 0.7,
                }}
            >
                Select to learn more
            </Typography>

            <Box display='flex' flexWrap='wrap' gap={1} mb={2}>
                {sortedKeywords.map((insight) => {
                    const total =
                        insight.positiveCount +
                        insight.negativeCount +
                        insight.neutralCount;
                    const style = getUnifiedKeywordStyle(insight);

                    return (
                        <Chip
                            key={insight.keyword}
                            label={`${capitalizeName(
                                insight.keyword,
                            )} (${total})`}
                            size='small'
                            onClick={() => handleKeywordClick(insight)}
                            sx={{
                                backgroundColor: style.backgroundColor,
                                border: `1px solid ${style.borderColor}`,
                                color: style.textColor,

                                fontSize: '12px',
                                '&:hover': {
                                    opacity: 0.8,
                                    transform: 'translateY(-1px)',
                                },
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                            }}
                        />
                    );
                })}
            </Box>

            {selectedKeyword && (
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.custom.modalActionBorder}`,
                        borderRadius: 2,
                        p: 3,
                        mt: 2,
                    }}
                >
                    <Box
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'
                        mb={2}
                    >
                        <Typography
                            variant='subtitle1'
                            sx={{
                                fontWeight: 'bold',
                                color: theme.palette.text.primary,
                            }}
                        >
                            {selectedKeyword.positiveCount +
                                selectedKeyword.negativeCount +
                                selectedKeyword.neutralCount}{' '}
                            guest
                            {selectedKeyword.positiveCount +
                                selectedKeyword.negativeCount +
                                selectedKeyword.neutralCount >
                            1
                                ? 's'
                                : ''}{' '}
                            mention
                            {selectedKeyword.positiveCount +
                                selectedKeyword.negativeCount +
                                selectedKeyword.neutralCount ===
                            1
                                ? 's'
                                : ''}{' '}
                            '{capitalizeName(selectedKeyword.keyword)}'
                        </Typography>
                        <IconButton
                            size='small'
                            onClick={() => setSelectedKeyword(null)}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box display='flex' gap={3} mb={3}>
                        {selectedKeyword.positiveCount > 0 && (
                            <Box display='flex' alignItems='center' gap={1}>
                                <ThumbUpOffAltIcon
                                    fontSize='small'
                                    sx={{
                                        fontSize: '16px',
                                        color: 'primary.main',
                                    }}
                                />

                                <Typography
                                    variant='body3'
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {selectedKeyword.positiveCount} positive
                                </Typography>
                            </Box>
                        )}
                        {selectedKeyword.negativeCount > 0 && (
                            <Box display='flex' alignItems='center' gap={1}>
                                <ThumbDownOffAltIcon
                                    fontSize='small'
                                    sx={{
                                        fontSize: '16px',
                                        color: theme.palette.warning.main,
                                    }}
                                />
                                <Typography
                                    variant='body3'
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {selectedKeyword.negativeCount} negative
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Box display='flex' flexDirection='column' gap={2}>
                        {getSampleReviews(selectedKeyword).map((review) => (
                            <SampleReviewItem
                                key={review.id}
                                review={review}
                                keyword={selectedKeyword.keyword}
                                onOpenModal={onShowMore}
                                theme={theme}
                            />
                        ))}
                    </Box>

                    <Box mt={3} display='flex' gap={1}>
                        {selectedKeyword.positiveCount > 0 && (
                            <Button
                                variant='outlined'
                                size='small'
                                onClick={() => {
                                    onKeywordClick(
                                        selectedKeyword.keyword,
                                        selectedKeyword.positiveIds,
                                        'positive',
                                        selectedKeyword.positiveCount,
                                        0,
                                    );
                                }}
                                sx={{
                                    fontSize: '12px',
                                    textTransform: 'none',
                                }}
                            >
                                View Positive ({selectedKeyword.positiveCount})
                            </Button>
                        )}
                        {selectedKeyword.negativeCount > 0 && (
                            <Button
                                variant='outlined'
                                size='small'
                                onClick={() => {
                                    onKeywordClick(
                                        selectedKeyword.keyword,
                                        selectedKeyword.negativeIds,
                                        'negative',
                                        0,
                                        selectedKeyword.negativeCount,
                                    );
                                }}
                                sx={{
                                    color: theme.palette.error.main,
                                    borderColor: theme.palette.error.main,
                                    fontSize: '12px',
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: theme.palette.error.main,
                                        backgroundColor:
                                            theme.palette.error.main + '10',
                                    },
                                }}
                            >
                                View Negative ({selectedKeyword.negativeCount})
                            </Button>
                        )}
                        {selectedKeyword.positiveCount > 0 &&
                            selectedKeyword.negativeCount > 0 && (
                                <Button
                                    variant='outlined'
                                    size='small'
                                    onClick={() => {
                                        const allIds = [
                                            ...selectedKeyword.positiveIds,
                                            ...selectedKeyword.negativeIds,
                                        ];
                                        onKeywordClick(
                                            selectedKeyword.keyword,
                                            allIds,
                                            selectedKeyword.positiveCount >=
                                                selectedKeyword.negativeCount
                                                ? 'positive'
                                                : 'negative',
                                            selectedKeyword.positiveCount,
                                            selectedKeyword.negativeCount,
                                        );
                                    }}
                                    sx={{
                                        color: 'text.primary',
                                        borderColor: 'text.primary',
                                        fontSize: '12px',
                                        textTransform: 'none',
                                        '&:hover': {
                                            borderColor: 'text.primary',
                                            backgroundColor:
                                                'text.primary' + '10',
                                        },
                                    }}
                                >
                                    View All (
                                    {selectedKeyword.positiveCount +
                                        selectedKeyword.negativeCount}
                                    )
                                </Button>
                            )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const SampleReviewItem: React.FC<{
    review: {
        id: number;
        reviewText: string;
        createdBy: {
            firstName: string;
            lastName: string;
        };
    };
    keyword: string;
    onOpenModal?: (reviewId: number) => void;
    theme: any;
}> = ({ review, keyword, onOpenModal, theme }) => {
    const maxLength = 150;
    const isLong = review.reviewText.length > maxLength;

    return (
        <Box
            sx={{
                p: 2,
                backgroundColor: theme.palette.custom.cardActionBackground,
                borderRadius: 1,
                border: `1px solid ${theme.palette.custom.modalActionBorder}`,
            }}
        >
            <Typography
                variant='caption'
                sx={{
                    color: theme.palette.text.primary,
                    fontSize: '11px',
                    fontWeight: 'bold',
                    mb: 0.5,
                    display: 'block',
                    opacity: 0.7,
                }}
            >
                {review.createdBy.firstName}
            </Typography>
            <Typography
                variant='body3'
                sx={{
                    color: theme.palette.text.primary,
                    fontSize: '13px',
                    lineHeight: 1.4,
                }}
            >
                {isLong
                    ? highlightText(
                          review.reviewText.substring(0, maxLength) + '...',
                          keyword,
                      )
                    : highlightText(review.reviewText, keyword)}
            </Typography>
            {isLong && (
                <Button
                    variant='text'
                    size='small'
                    onClick={() => onOpenModal?.(review.id)}
                    sx={{
                        color: theme.palette.primary.main,
                        fontSize: '11px',
                        textTransform: 'none',
                        mt: 0.5,
                        p: 0,
                        minHeight: 'auto',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            color: theme.palette.primary.dark,
                            textDecoration: 'underline',
                        },
                    }}
                >
                    Read more{' '}
                    <ArrowForwardIosIcon
                        sx={{
                            fontSize: '8px',
                            ml: 0.1,
                            mt: 0.3,
                        }}
                    />
                </Button>
            )}
        </Box>
    );
};

export default AiReviewSummary;
